import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import api from '../services/api';
import './FinalGameInterface.css';

function FinalGameInterface({ section }) {
  const { selectedTeam, currentSession, confirmFinalGameAnswer, submitFinalGameAnswer, moveToNextFinalGameQuestion, finalGameWinner, socket } = useGame();
  
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [confirmedAnswer, setConfirmedAnswer] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(15); // 20 seconds per question
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [isAnswerConfirmed, setIsAnswerConfirmed] = useState(false);
  const [timerActive, setTimerActive] = useState(true);
  const [showWinner, setShowWinner] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const timerRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Fetch final game questions (same 10 questions for all tablets)
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await api.get('/question/final-game');
        if (response.data.success && response.data.questions.length > 0) {
          console.log('Fetched final game questions:', response.data.questions.length);
          setQuestions(response.data.questions);
          setCurrentQuestionIndex(0);
          setTimeRemaining(15);
          setTimerActive(true);
          isProcessingRef.current = false;
        } else {
          console.error('No final game questions found');
        }
      } catch (error) {
        console.error('Error fetching final game questions:', error);
      }
    };

    // Only fetch if final game is active
    if (currentSession?.finalGameActive) {
      fetchQuestions();
    }
  }, [currentSession?.finalGameActive]);

  // Handle timer end - useCallback to prevent stale closures
  const handleTimerEnd = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isProcessingRef.current || loading || completed) {
      return;
    }

    isProcessingRef.current = true;
    setTimerActive(false);
    setLoading(true);

    try {
      // Get current question using functional update to ensure we have latest state
      const currentQ = questions[currentQuestionIndex];
      if (!currentQ) {
        console.error('No current question found');
        setLoading(false);
        isProcessingRef.current = false;
        return;
      }

      // Submit answer (use confirmed answer if available, otherwise selected answer)
      const answerToSubmit = confirmedAnswer || selectedAnswer || '';
      console.log('Submitting answer for question:', currentQ._id, 'Answer:', answerToSubmit);
      
      const result = await submitFinalGameAnswer(currentQ._id, answerToSubmit);

      if (result.error) {
        console.error('Error submitting answer:', result.error);
        setLoading(false);
        setTimerActive(true);
        isProcessingRef.current = false;
        return;
      }

      console.log('Answer submitted successfully, moving to next question...');

      // Move to next question
      const nextResult = await moveToNextFinalGameQuestion();
      
      if (nextResult.error) {
        console.error('Error moving to next question:', nextResult.error);
        setLoading(false);
        setTimerActive(true);
        isProcessingRef.current = false;
        return;
      }

      console.log('Next question result:', nextResult);

      // Check if game is complete
      if (nextResult.isComplete || !nextResult.question) {
        // All questions completed for this team
        console.log('All questions completed for this team');
        setCompleted(true);
        setTimerActive(false);
        // The results screen will be shown automatically when final game ends
      } else {
        // Update to the next question index from server response
        const newIndex = nextResult.questionIndex !== undefined ? nextResult.questionIndex : currentQuestionIndex + 1;
        console.log('Moving to question index:', newIndex);
        
        // Reset state for next question
        setCurrentQuestionIndex(newIndex);
        setSelectedAnswer('');
        setConfirmedAnswer('');
        setIsAnswerConfirmed(false);
        setTimeRemaining(15);
        
        // Small delay to ensure state updates before restarting timer
        setTimeout(() => {
          setTimerActive(true);
          setLoading(false);
          isProcessingRef.current = false;
        }, 100);
      }
    } catch (error) {
      console.error('Error in timer end handler:', error);
      setLoading(false);
      setTimerActive(true);
      isProcessingRef.current = false;
    }
  }, [questions, currentQuestionIndex, confirmedAnswer, selectedAnswer, loading, completed, submitFinalGameAnswer, moveToNextFinalGameQuestion]);

  // Timer countdown - 20 seconds per question
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!timerActive || completed || questions.length === 0 || loading || isProcessingRef.current) {
      return;
    }

    // Start new timer
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // Timer ended - submit answer and move to next question
          handleTimerEnd();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timerActive, completed, questions.length, loading, handleTimerEnd]);

  const handleConfirmAnswer = async () => {
    if (!selectedAnswer || isAnswerConfirmed || loading) return;

    setLoading(true);
    try {
      const result = await confirmFinalGameAnswer(selectedAnswer);
      if (result.error) {
        console.error('Error confirming answer:', result.error);
      } else {
        setConfirmedAnswer(selectedAnswer);
        setIsAnswerConfirmed(true);
      }
    } catch (error) {
      console.error('Error confirming answer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Listen for final game ended event
  useEffect(() => {
    if (!socket) return;

    const handleFinalGameEnded = (data) => {
      console.log('🏆 Final game ended, winner:', data.winner);
      setShowWinner(true);
      setCompleted(true);
      setTimerActive(false);
      
      // After 10 seconds, show loading screen
      setTimeout(() => {
        setShowWinner(false);
        setShowLoading(true);
      }, 10000);
    };

    socket.on('final-game:ended', handleFinalGameEnded);

    return () => {
      socket.off('final-game:ended', handleFinalGameEnded);
    };
  }, [socket]);

  // Show loading screen after winner announcement
  if (showLoading) {
    return (
      <div className="final-game-interface loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <h1>Preparing Next Game...</h1>
          <p>Waiting for gamemaster to start a new game...</p>
        </div>
      </div>
    );
  }

  // Show winner announcement
  if (showWinner && finalGameWinner) {
    const isWinner = selectedTeam?.id === finalGameWinner.teamId?.toString() || 
                     selectedTeam?.name === finalGameWinner.teamName;
    
    return (
      <div className="final-game-interface winner-screen">
        <div className="winner-content">
          <div className="trophy-icon">🏆</div>
          <h1 className="winner-title">Final Game Complete!</h1>
          <div className="winner-announcement">
            <h2 className="winner-name">{finalGameWinner.teamName}</h2>
            <p className="winner-label">is the Winner!</p>
            <p className="winner-score">Final Score: {finalGameWinner.score} points</p>
          </div>
          {isWinner && (
            <div className="congratulations">
              <p>🎉 Congratulations! 🎉</p>
            </div>
          )}
          <p className="instruction">Preparing for next game...</p>
        </div>
      </div>
    );
  }

  // Show completed state (fallback, should not normally show)
  if (completed && !showWinner) {
    return (
      <div className="final-game-interface completed-screen">
        <div className="completed-content">
          <div className="spinner"></div>
          <h1>Final Game Complete!</h1>
          <p>Waiting for results...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="final-game-interface">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading final game...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="final-game-interface">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading question...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="final-game-interface">
      <div className="final-game-header">
        <div className="header-left">
          <h1>🏆 Final Game</h1>
          <p className="team-name">{selectedTeam?.name}</p>
        </div>
        <div className="progress-info">
          <span className="question-counter">
            Question {currentQuestionIndex + 1} / {questions.length}
          </span>
          <div className="timer-display-final">
            <span className="timer-icon">⏱️</span>
            <span className={`timer-value ${timeRemaining <= 5 ? 'critical' : ''}`}>
              {timeRemaining}s
            </span>
          </div>
        </div>
      </div>

      <div className="final-game-content">
        <div className="final-question-card">
          <h2 className="final-question-text">
            {currentQuestion.questionText}
          </h2>

          <div className="final-options-grid">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === option.text;
              const isConfirmed = confirmedAnswer === option.text;
              return (
                <button
                  key={index}
                  className={`final-option-button ${
                    isSelected ? 'selected' : ''
                  } ${
                    isConfirmed ? 'confirmed' : ''
                  }`}
                  onClick={() => {
                    if (!isAnswerConfirmed && !loading && timerActive) {
                      setSelectedAnswer(option.text);
                    }
                  }}
                  disabled={isAnswerConfirmed || loading || !timerActive}
                >
                  {option.text}
                  {isConfirmed && <span className="confirmed-badge">✓</span>}
                </button>
              );
            })}
          </div>

          {!isAnswerConfirmed && selectedAnswer && (
            <button 
              onClick={handleConfirmAnswer} 
              className="btn-gold confirm-btn-final"
              disabled={loading || !timerActive}
            >
              {loading ? 'Confirming...' : 'Confirm Answer'}
            </button>
          )}

          {isAnswerConfirmed && (
            <div className="answer-confirmed-message">
              <p>✓ Answer confirmed. Waiting for timer to end...</p>
            </div>
          )}

          {loading && (
            <div className="loading-message">
              <div className="spinner"></div>
              <p>Processing...</p>
            </div>
          )}
        </div>
      </div>

      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
        ></div>
      </div>
    </div>
  );
}

export default FinalGameInterface;
